import type { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { ProgressBar } from "@/components/ui/progress-bar";
import {
  ModuleRow,
  type ModuleProgressInfo,
} from "@/components/dashboard/ModuleRow";

export interface ContinueLearningCardProps {
  courseTitle: string;
  progress: number; // 0-100
  onClick: () => void;
  firstName: string;
  isReturningUser: boolean;
  isCompleted?: boolean;
  breadcrumb?: string | null;
  modules?: ModuleProgressInfo[];
  totalModulesComplete?: number;
  totalModulesOpen?: number;
  /** Shown between the greeting and the progress panel. */
  afterGreeting?: ReactNode;
}

type GreetingParams = Pick<
  ContinueLearningCardProps,
  "courseTitle" | "firstName" | "isReturningUser" | "isCompleted"
>;

/** Finished once the completion quiz is passed, or once every lesson is done. */
export const courseIsCompleted = (
  status: string | undefined,
  progress: number
): boolean => status === "completed" || progress >= 100;

export const getGreeting = ({
  courseTitle,
  firstName,
  isReturningUser,
  isCompleted = false,
}: GreetingParams): string => {
  if (isCompleted) {
    return firstName
      ? `Glückwunsch, ${firstName}! Kurs abgeschlossen.`
      : "Glückwunsch! Kurs abgeschlossen.";
  }

  if (isReturningUser) {
    return firstName
      ? `Willkommen zurück, ${firstName}!`
      : "Willkommen zurück!";
  }

  return firstName
    ? `Willkommen zum Kurs ${courseTitle}, ${firstName}!`
    : `Willkommen zum Kurs ${courseTitle}!`;
};

export const ContinueCard = ({
  courseTitle,
  progress,
  onClick,
  firstName,
  isReturningUser,
  isCompleted = false,
  breadcrumb,
  modules,
  totalModulesComplete = 0,
  totalModulesOpen = 0,
  afterGreeting,
}: ContinueLearningCardProps) => {
  const hasModules = Boolean(modules?.length);

  const greeting = getGreeting({
    courseTitle,
    firstName,
    isReturningUser,
    isCompleted,
  });

  let buttonLabel = "Mit dem Kurs starten";
  if (isCompleted) {
    buttonLabel = "Kurs ansehen";
  } else if (isReturningUser) {
    buttonLabel = "Weiter lernen";
  }

  return (
    <div>
      <Card
        className="cursor-pointer overflow-hidden py-0 gap-0"
        onClick={onClick}
      >
        <div className="flex-1 min-w-0 flex bg-linear-to-br from-primary to-primary/80 text-white">
          <div className="flex-1 min-w-0 px-4 py-3 flex flex-col gap-1">
            <div className="text-lg font-semibold">{greeting}</div>
            {isReturningUser && breadcrumb && (
              <div className="text-xs text-white/70 truncate">{breadcrumb}</div>
            )}
            <div className="mt-2">
              <span className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-full px-4 py-2 text-sm font-semibold transition-colors">
                {buttonLabel} <span>→</span>
              </span>
            </div>
          </div>
        </div>
      </Card>

      {afterGreeting}

      {/* Progress panel */}
      {hasModules && (
        <div className="bg-white border rounded-xl shadow-sm px-5 py-4 mt-4">
          {progress > 0 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-muted-foreground">
                  Mein Fortschritt
                </span>
                <div className="flex items-center gap-4 text-xs">
                  {totalModulesComplete > 0 && (
                    <span className="font-semibold text-success-foreground">
                      {totalModulesComplete}{" "}
                      {totalModulesComplete === 1 ? "Modul" : "Module"} erledigt
                    </span>
                  )}
                  <span className="font-semibold text-muted-foreground">
                    {totalModulesOpen}{" "}
                    {totalModulesOpen === 1 ? "Modul" : "Module"} offen
                  </span>
                </div>
              </div>
              <div className="mb-3">
                <div className="text-2xl font-bold text-primary">
                  {progress}%{" "}
                  <span className="text-sm font-bold tracking-wide text-muted-foreground">
                    abgeschlossen
                  </span>
                </div>
                <ProgressBar
                  percentage={progress}
                  size="lg"
                  color={progress >= 100 ? "success" : "secondary"}
                  showLabel={false}
                  className="mt-2"
                />
              </div>
            </>
          )}
          <div className="flex flex-col gap-1">
            {modules?.map((mod) => (
              <ModuleRow key={mod.title} module={mod} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
